import {
  TableCellsIcon,
  EyeIcon,
  CodeBracketIcon,
  CogIcon,
  ChartBarIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  PencilIcon,
  UserIcon,
  BoltIcon,
  RectangleStackIcon,
  FolderIcon,
  CommandLineIcon,
  CircleStackIcon,
  ClockIcon,
  PuzzlePieceIcon,
  HashtagIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  PlayIcon,
  PlusCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import type { ContextMenuItem } from './ContextMenu'

// Define specific types for context menu action data
export type ContextMenuActionData = 
  | { database: string }
  | { database: string; schema: string }
  | { database: string; table: string; schema?: string }
  | { database: string; view: string; schema?: string }
  | { database: string; function: string; schema?: string; arguments?: string }
  | { database: string; procedure: string; schema?: string }
  | { database: string; index: string; schema?: string }
  | { database: string; trigger: string; schema?: string }
  | { database: string; sequence: string; schema?: string }
  | { database: string; event: string; schema?: string }
  | { database: string; extension: string; schema?: string }
  | { database: string; user: string; schema?: string }
  | { database: string; schema?: string; folderType: string }
  | { type: 'table' | 'view'; database: string; schema?: string; name: string }
  | { selectedItems: string[] }
  | { selectedItems: { name: string; type: string; database?: string; schema?: string }[] }
  | Record<string, never> // For actions with no data

export type ContextMenuActionHandler = (action: string, data?: ContextMenuActionData) => void

// Constants to avoid duplicate string literals
const SEPARATOR_LABEL = ''
const SEPARATOR_ONCLICK = () => {}
const SEPARATOR_1_ID = 'separator-1'
const SEPARATOR_2_ID = 'separator-2'
const SEPARATOR_3_ID = 'separator-3'
const SEPARATOR_4_ID = 'separator-4'
const SEPARATOR_0_ID = 'separator-0'

// Context menu builder helpers
export function buildDatabaseContextMenu(
  database: string,
  onAction: ContextMenuActionHandler,
  databaseType?: string
): ContextMenuItem[] {
  // For PostgreSQL, only show "Create Schema" option
  if (databaseType === 'PostgreSQL') {
    return [
      {
        id: 'new-schema',
        label: 'Create Schema',
        icon: FolderIcon,
        onClick: () => { onAction('create-schema', { database }) }
      },
      {
        id: SEPARATOR_1_ID,
        label: SEPARATOR_LABEL,
        separator: true,
        onClick: SEPARATOR_ONCLICK
      },
      {
        id: 'view-diagram',
        label: 'View Database Diagram',
        icon: ChartBarIcon,
        onClick: () => { onAction('view-diagram', { database }) }
      },
      {
        id: SEPARATOR_2_ID,
        label: SEPARATOR_LABEL,
        separator: true,
        onClick: SEPARATOR_ONCLICK
      },
      {
        id: 'refresh',
        label: 'Refresh',
        icon: ArrowPathIcon,
        onClick: () => { onAction('refresh', { database }) }
      },
      {
        id: SEPARATOR_3_ID,
        label: SEPARATOR_LABEL,
        separator: true,
        onClick: SEPARATOR_ONCLICK
      },
      {
        id: 'drop-database',
        label: 'Drop Database',
        icon: TrashIcon,
        variant: 'danger',
        onClick: () => { onAction('drop-database', { database }) }
      }
    ]
  }

  // For other database types, show the default menu items
  return [
    {
      id: 'new-table',
      label: 'Create Table',
      icon: TableCellsIcon,
      onClick: () => { onAction('create-table', { database }) }
    },
    {
      id: 'new-view',
      label: 'Create View',
      icon: EyeIcon,
      onClick: () => { onAction('create-view', { database }) }
    },
    {
      id: 'new-function',
      label: 'Create Function',
      icon: CodeBracketIcon,
      onClick: () => { onAction('create-function', { database }) }
    },
    {
      id: 'new-procedure',
      label: 'Create Procedure',
      icon: CogIcon,
      onClick: () => { onAction('create-procedure', { database }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'view-diagram',
      label: 'View Database Diagram',
      icon: ChartBarIcon,
      onClick: () => { onAction('view-diagram', { database }) }
    },
    {
      id: SEPARATOR_2_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: ArrowPathIcon,
      onClick: () => { onAction('refresh', { database }) }
    },
    {
      id: SEPARATOR_3_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-database',
      label: 'Drop Database',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-database', { database }) }
    }
  ]
}

export function buildSchemaContextMenu(
  database: string,
  schema: string,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'new-table',
      label: 'Create Table',
      icon: TableCellsIcon,
      onClick: () => { onAction('create-table', { database, schema }) }
    },
    {
      id: 'new-view',
      label: 'Create View',
      icon: EyeIcon,
      onClick: () => { onAction('create-view', { database, schema }) }
    },
    {
      id: 'new-function',
      label: 'Create Function',
      icon: CodeBracketIcon,
      onClick: () => { onAction('create-function', { database, schema }) }
    },
    {
      id: 'new-procedure',
      label: 'Create Procedure',
      icon: CogIcon,
      onClick: () => { onAction('create-procedure', { database, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'view-diagram',
      label: 'View Schema Diagram',
      icon: ChartBarIcon,
      onClick: () => { onAction('view-diagram', { database, schema }) }
    },
    {
      id: SEPARATOR_2_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: ArrowPathIcon,
      onClick: () => { onAction('refresh', { database, schema }) }
    },
    {
      id: SEPARATOR_3_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-schema',
      label: 'Drop Schema',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-schema', { database, schema }) }
    }
  ]
}

export function buildTableContextMenu(
  database: string,
  table: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'select-query',
      label: 'Select Query',
      icon: CommandLineIcon,
      onClick: () => { onAction('select-query', { database, table, schema }) }
    },
    {
      id: 'insert-query',
      label: 'Insert Query',
      icon: PlusCircleIcon,
      onClick: () => { onAction('insert-query', { database, table, schema }) }
    },
    {
      id: 'analyze-table',
      label: 'Analyze',
      icon: MagnifyingGlassIcon,
      onClick: () => { onAction('analyze-table', { database, table, schema }) }
    },
    {
      id: SEPARATOR_0_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'edit-table',
      label: 'Edit Table',
      icon: PencilIcon,
      onClick: () => { onAction('edit-table', { database, table, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'create-index',
      label: 'Create Index',
      icon: RectangleStackIcon,
      onClick: () => { onAction('create-index', { database, table, schema }) }
    },
    {
      id: 'create-trigger',
      label: 'Create Trigger',
      icon: BoltIcon,
      onClick: () => { onAction('create-trigger', { database, table, schema }) }
    },
    {
      id: SEPARATOR_2_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'export-data',
      label: 'Export Data',
      icon: DocumentArrowDownIcon,
      onClick: () => { onAction('export-data', { database, table, schema }) }
    },
    {
      id: SEPARATOR_3_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: ArrowPathIcon,
      onClick: () => { onAction('refresh', { type: 'table', database, schema, name: table }) }
    },
    {
      id: SEPARATOR_4_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-table',
      label: 'Drop Table',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-table', { database, table, schema }) }
    }
  ]
}

export function buildFolderContextMenu(
  folderType: string,
  database: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  const items: ContextMenuItem[] = []

  switch (folderType) {
    case 'tables-folder':
      items.push({
        id: 'new-table',
        label: 'Create Table',
        icon: TableCellsIcon,
        onClick: () => { onAction('create-table', { database, schema }) }
      })
      break
    case 'views-folder':
      items.push({
        id: 'new-view',
        label: 'Create View',
        icon: EyeIcon,
        onClick: () => { onAction('create-view', { database, schema }) }
      })
      break
    case 'functions-folder':
      items.push({
        id: 'new-function',
        label: 'Create Function',
        icon: CodeBracketIcon,
        onClick: () => { onAction('create-function', { database, schema }) }
      })
      break
    case 'procedures-folder':
      items.push({
        id: 'new-procedure',
        label: 'Create Procedure',
        icon: CogIcon,
        onClick: () => { onAction('create-procedure', { database, schema }) }
      })
      break
    case 'users-folder':
      items.push({
        id: 'new-user',
        label: 'Create User',
        icon: UserIcon,
        onClick: () => { onAction('create-user', { database, schema }) }
      })
      break
    case 'indexes-folder':
      items.push({
        id: 'new-index',
        label: 'Create Index',
        icon: RectangleStackIcon,
        onClick: () => { onAction('create-index', { database, schema }) }
      })
      break
    case 'events-folder':
      items.push({
        id: 'new-event',
        label: 'Create Event',
        icon: ClockIcon,
        onClick: () => { onAction('create-event', { database, schema }) }
      })
      break
    case 'extensions-folder':
      items.push({
        id: 'new-extension',
        label: 'Install Extension',
        icon: PuzzlePieceIcon,
        onClick: () => { onAction('create-extension', { database, schema }) }
      })
      break
    case 'sequences-folder':
      items.push({
        id: 'new-sequence',
        label: 'Create Sequence',
        icon: HashtagIcon,
        onClick: () => { onAction('create-sequence', { database, schema }) }
      })
      break
    case 'triggers-folder':
      items.push({
        id: 'new-trigger',
        label: 'Create Trigger',
        icon: BoltIcon,
        onClick: () => { onAction('create-trigger', { database, schema }) }
      })
      break
  }

  items.push(
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: ArrowPathIcon,
      onClick: () => { onAction('refresh', { database, schema, folderType }) }
    }
  )

  return items
}

export function buildViewContextMenu(
  database: string,
  view: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'select-query',
      label: 'Select Query',
      icon: CommandLineIcon,
      onClick: () => { onAction('select-query', { database, table: view, schema }) }
    },
    {
      id: SEPARATOR_0_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'edit-view',
      label: 'Edit View',
      icon: PencilIcon,
      onClick: () => { onAction('edit-view', { database, view, schema }) }
    },
    {
      id: 'duplicate-view',
      label: 'Duplicate View',
      icon: DocumentDuplicateIcon,
      onClick: () => { onAction('duplicate-view', { database, view, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'export-data',
      label: 'Export Data',
      icon: DocumentArrowDownIcon,
      onClick: () => { onAction('export-data', { database, view, schema }) }
    },
    {
      id: SEPARATOR_2_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'refresh',
      label: 'Refresh',
      icon: ArrowPathIcon,
      onClick: () => { onAction('refresh', { type: 'view', database, schema, name: view }) }
    },
    {
      id: SEPARATOR_3_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-view',
      label: 'Drop View',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-view', { database, view, schema }) }
    }
  ]
}

export function buildFunctionContextMenu(
  database: string,
  functionName: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler,
  functionArguments?: string
): ContextMenuItem[] {
  return [
    {
      id: 'edit-function',
      label: 'Edit Function',
      icon: PencilIcon,
      onClick: () => { onAction('edit-function', { database, function: functionName, schema, arguments: functionArguments }) }
    },
    {
      id: 'execute-function',
      label: 'Execute Function',
      icon: PlayIcon,
      onClick: () => { onAction('execute-function', { database, function: functionName, schema, arguments: functionArguments }) }
    },
    {
      id: 'duplicate-function',
      label: 'Duplicate Function',
      icon: DocumentDuplicateIcon,
      onClick: () => { onAction('duplicate-function', { database, function: functionName, schema, arguments: functionArguments }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-function',
      label: 'Drop Function',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-function', { database, function: functionName, schema, arguments: functionArguments }) }
    }
  ]
}

export function buildProcedureContextMenu(
  database: string,
  procedure: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'edit-procedure',
      label: 'Edit Procedure',
      icon: PencilIcon,
      onClick: () => { onAction('edit-procedure', { database, procedure, schema }) }
    },
    {
      id: 'execute-procedure',
      label: 'Execute Procedure',
      icon: PlayIcon,
      onClick: () => { onAction('execute-procedure', { database, procedure, schema }) }
    },
    {
      id: 'duplicate-procedure',
      label: 'Duplicate Procedure',
      icon: DocumentDuplicateIcon,
      onClick: () => { onAction('duplicate-procedure', { database, procedure, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-procedure',
      label: 'Drop Procedure',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-procedure', { database, procedure, schema }) }
    }
  ]
}

export function buildIndexContextMenu(
  database: string,
  index: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'view-index',
      label: 'View Index Definition',
      icon: EyeIcon,
      onClick: () => { onAction('view-index', { database, index, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-index',
      label: 'Drop Index',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-index', { database, index, schema }) }
    }
  ]
}

export function buildTriggerContextMenu(
  database: string,
  trigger: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'edit-trigger',
      label: 'Edit Trigger',
      icon: PencilIcon,
      onClick: () => { onAction('edit-trigger', { database, trigger, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-trigger',
      label: 'Drop Trigger',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-trigger', { database, trigger, schema }) }
    }
  ]
}

export function buildSequenceContextMenu(
  database: string,
  sequence: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'edit-sequence',
      label: 'Edit Sequence',
      icon: PencilIcon,
      onClick: () => { onAction('edit-sequence', { database, sequence, schema }) }
    },
    {
      id: 'restart-sequence',
      label: 'Restart Sequence',
      icon: ArrowPathIcon,
      onClick: () => { onAction('restart-sequence', { database, sequence, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-sequence',
      label: 'Drop Sequence',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-sequence', { database, sequence, schema }) }
    }
  ]
}

export function buildUserContextMenu(
  database: string,
  user: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'edit-user',
      label: 'Edit User',
      icon: PencilIcon,
      onClick: () => { onAction('edit-user', { database, user, schema }) }
    },
    {
      id: 'change-password',
      label: 'Change Password',
      icon: CogIcon,
      onClick: () => { onAction('change-password', { database, user, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-user',
      label: 'Drop User',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-user', { database, user, schema }) }
    }
  ]
}

export function buildMultiSelectContextMenu(
  count: number,
  onAction: (action: string) => void,
  selectedTypes?: string[]
): ContextMenuItem[] {
  const hasDatabases = selectedTypes?.includes('database') || false
  const hasSchemas = selectedTypes?.includes('schema') || false
  const hasDangerousItems = hasDatabases || hasSchemas
  
  // Count analyzable items (tables and views)
  const analyzableTypes = ['table', 'view']
  const analyzableCount = selectedTypes?.filter(type => analyzableTypes.includes(type)).length || 0
  
  const dropLabel = hasDatabases 
    ? `Drop ${count} Selected Items (includes databases!)`
    : hasSchemas
    ? `Drop ${count} Selected Items (includes schemas!)`
    : `Drop ${count} Selected Items`

  const items: ContextMenuItem[] = []
  
  // Only show analyze option if there are analyzable items (tables/views)
  if (analyzableCount > 0) {
    items.push({
      id: 'analyze-multiple',
      label: `Analyze ${analyzableCount} ${analyzableCount === 1 ? 'Table' : 'Tables'}`,
      icon: MagnifyingGlassIcon,
      onClick: () => { onAction('analyze-multiple') }
    })
  }
  
  items.push(
    {
      id: 'drop-multiple',
      label: dropLabel,
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-multiple') }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'export-multiple',
      label: `Export ${count} Items`,
      icon: DocumentArrowDownIcon,
      onClick: () => { onAction('export-multiple') },
      disabled: hasDangerousItems // Disable export for databases/schemas for now
    }
  )
  
  return items
}

export function buildDatabasesRootContextMenu(
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'create-database',
      label: 'Create Database',
      icon: CircleStackIcon,
      onClick: () => { onAction('create-database') }
    }
  ]
}

export function buildEventContextMenu(
  database: string,
  event: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'edit-event',
      label: 'Edit Event',
      icon: PencilIcon,
      onClick: () => { onAction('edit-event', { database, event, schema }) }
    },
    {
      id: 'duplicate-event',
      label: 'Duplicate Event',
      icon: DocumentDuplicateIcon,
      onClick: () => { onAction('duplicate-event', { database, event, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-event',
      label: 'Drop Event',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-event', { database, event, schema }) }
    }
  ]
}

export function buildExtensionContextMenu(
  database: string,
  extension: string,
  schema: string | undefined,
  onAction: ContextMenuActionHandler
): ContextMenuItem[] {
  return [
    {
      id: 'extension-info',
      label: 'Extension Info',
      icon: DocumentDuplicateIcon,
      onClick: () => { onAction('extension-info', { database, extension, schema }) }
    },
    {
      id: SEPARATOR_1_ID,
      label: '',
      separator: true,
      onClick: () => {}
    },
    {
      id: 'drop-extension',
      label: 'Drop Extension',
      icon: TrashIcon,
      variant: 'danger',
      onClick: () => { onAction('drop-extension', { database, extension, schema }) }
    }
  ]
} 